import React, { useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { JOB_TYPE_MEDIA, srJobTypeTextMapping } from 'common/jobConstants';

import useBranch from 'client/hooks/useBranch';

import SrFlex from 'client/components/SrFlex';
import SrFlexRow from 'client/components/SrFlexRow/SrFlexRow';
import SrText from 'client/components/SrText';
import SrQualifiedListItem from 'client/components/SrQualifiedListItem/SrQualifiedListItem';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';

const SetupHealthChecks = ({
  setupHealth,
  listingId,
}) => {
  const { jistixUrl } = useBranch({
    jistixUrl: ['env', 'SIGN_OTTER_API_URL'],
  });

  const {
    checks:{
      orderCreated,
      orderAssigned,
      orderAccepted,
      orderItemsInstalled,
      orderDuePromiseDate,
      orderCompletedOnTime,
    } = {},
  } = setupHealth;

  const [
    displayCreatedOrderDetail,
    setDisplayCreatedOrderDetail,
  ] = useState(!orderCreated);

  const [
    displayAssignedOrderDetail,
    setDisplayAssignedOrderDetail,
  ] = useState(!orderAssigned);

  const [
    displayAcceptedOrderDetail,
    setDisplayAcceptedOrderDetail,
  ] = useState(!orderAccepted);

  const [
    displayInstalledOrderDetail,
    setDisplayInstalledOrderDetail,
  ] = useState(!orderItemsInstalled);

  const [
    displayDueOnTimeDetail,
    setDisplayDueOnTimeDetail,
  ] = useState(!orderDuePromiseDate);

  const [
    displayCompletedOnTimeDetail,
    setDisplayCompletedOnTimeDetail,
  ] = useState(!orderCompletedOnTime);

  const _getInstallLinkComponent = ({ lowerCase = false, signOtterId }) => {
    const {
      installation: {
        isRequired: installationRequired,
        signOtterId: installationOrderId,
      } = {},
    } = setupHealth;

    if (!installationRequired) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {
            lowerCase
              ? 'install'
              : 'Install'
          }
        </SrText>
      );
    }

    const installationId = signOtterId || installationOrderId;

    return (
      <SrLink to={`${jistixUrl}orders/${installationId}`} target='_blank'>
        <SrText
          font='greycliff'
          weight='600'
          color='blue300'
          cursor='pointer'
        >
          {
            lowerCase
              ? 'install'
              : 'Install'
          }
        </SrText>
      </SrLink>
    );
  };

  const _getMediaLinkComponent = () => {
    const {
      media: {
        isRequired: mediaRequired,
        signOtterId: mediaOrderId,
      } = {},
    } = setupHealth;

    if (!mediaRequired || !mediaOrderId) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          media
        </SrText>
      );
    }

    return (
      <SrLink to={`${jistixUrl}orders/${mediaOrderId}`} target='_blank'>
        <SrText
          font='greycliff'
          weight='600'
          color='blue300'
          cursor='pointer'
        >
          media
        </SrText>
      </SrLink>
    );
  };

  // Created Order
  const _getCreatedOrderDetail = () => {
    const {
      media:{
        isRequired: mediaRequired,
        isCreated: mediaCreated,
      } = {},
      installation:{
        isRequired: installationRequired,
        isCreated: installationCreated,
      } = {},
    } = setupHealth;

    const installationLink = _getInstallLinkComponent({});
    const mediaLink = _getMediaLinkComponent();

    if (!mediaRequired && !installationRequired) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          Install and media not required
        </SrText>
      );
    }

    if (mediaCreated && installationCreated) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} and {mediaLink} created
        </SrText>
      );
    }

    if (mediaRequired || installationRequired) {
      let installDetail = '';
      let mediaDetail = '';

      if (!installationRequired) {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} not requested,&nbsp;
          </SrText>
        );
      }
      else if (installationCreated) {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} created,&nbsp;
          </SrText>
        );
      }
      else {
        installDetail = 'Create another install to complete the setup, ';
      }

      if (!mediaRequired) {
        mediaDetail = 'media not requested';
      }
      else if (mediaCreated) {
        mediaDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {mediaLink} created
          </SrText>
        );
      }
      else {
        mediaDetail = 'media not found';
      }

      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installDetail}
          {mediaDetail}
        </SrText>
      );
    }
  };

  const _getCreatedOrderCheckComponent = () => {
    const {
      checks:{
        orderCreated: isQualified,
      } = {},
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayCreatedOrderDetail(!displayCreatedOrderDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getCreatedOrderDetail();
    return (
      <SrFlexRow key={`${listingId}_orderCreatedCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_orderCreated`}
          title='Created Order'
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayCreatedOrderDetail && (
          <SrFlexRow
            key={`${listingId}_orderCreated_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              <SrText
                font='greycliff'
                weight='600'
                color='grey'
              >
                ({detail})
              </SrText>
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  // Assigned Order
  const _getAssignedOrderDetail = () => {
    const {
      media:{
        isRequired: mediaRequired,
        isAssigned: mediaAssigned,
      } = {},
      installation:{
        isRequired: installationRequired,
        isCreated: installationCreated,
        isAssigned: installationAssigned,
      } = {},
    } = setupHealth;

    const installationLink = _getInstallLinkComponent({});
    const mediaLink = _getMediaLinkComponent();

    if (!mediaRequired && !installationRequired) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          Install and media not required
        </SrText>
      );
    }

    if (
      mediaRequired
      && installationRequired
      && mediaAssigned
      && installationAssigned
    ) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} and {mediaLink} assigned
        </SrText>
      );
    }

    if (
      mediaRequired
      && installationRequired
      && !mediaAssigned
      && !installationAssigned
    ) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} and {mediaLink} not assigned
        </SrText>
      );
    }

    if (mediaRequired || installationRequired) {
      let installDetail = '';
      let mediaDetail = '';

      if (!installationRequired) {
        installDetail = 'Install not requested,';
      }
      else if (!installationCreated) {
        installDetail = 'Create another install to complete the setup,';
      }
      else if (!installationAssigned) {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} not assigned,
          </SrText>
        );
      }
      else {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} assigned,
          </SrText>
        );
      }

      if (!mediaRequired) {
        mediaDetail = 'media not requested';
      }
      else if (!mediaAssigned) {
        mediaDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {mediaLink} not assigned
          </SrText>
        );
      }
      else {
        mediaDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {mediaLink} assigned
          </SrText>
        );
      }

      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installDetail}&nbsp;{mediaDetail}
        </SrText>
      );
    }
  };

  const _getAssignedOrderCheckComponent = () => {
    const {
      checks:{
        orderAssigned: isQualified,
      } = {},
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayAssignedOrderDetail(!displayAssignedOrderDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getAssignedOrderDetail();

    return (
      <SrFlexRow key={`${listingId}_orderAssignedCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_orderAssigned`}
          title='Assigned Order'
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayAssignedOrderDetail && (
          <SrFlexRow
            key={`${listingId}_orderAssigned_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              <SrText
                font='greycliff'
                weight='600'
                color='grey'
              >
                ({detail})
              </SrText>
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  // Driver Accepted Order
  const _getDriverAcceptedOrderDetail = () => {
    const {
      media:{
        isRequired: mediaRequired,
        isAcceptedByDriver: mediaAcceptedByDriver,
      } = {},
      installation:{
        isRequired: installationRequired,
        isCreated: installationCreated,
        isAcceptedByDriver: installationAcceptedByDriver,
      } = {},
    } = setupHealth;

    const installationLink = _getInstallLinkComponent({});
    const mediaLink = _getMediaLinkComponent();

    if (!mediaRequired && !installationRequired) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          Install and media not required
        </SrText>
      );
    }

    if (
      mediaRequired
      && installationRequired
      && installationCreated
      && mediaAcceptedByDriver
      && installationAcceptedByDriver
    ) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} and {mediaLink} accepted
        </SrText>
      );
    }

    if (
      mediaRequired
      && installationRequired
      && installationCreated
      && !mediaAcceptedByDriver
      && !installationAcceptedByDriver
    ) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} and {mediaLink} not accepted
        </SrText>
      );
    }

    if (mediaRequired || installationRequired) {
      let installDetail = '';
      let mediaDetail = '';

      if (!installationRequired) {
        installDetail = 'Install not requested, ';
      }
      else if (!installationCreated) {
        installDetail = 'Create another install to complete the setup,';
      }
      else if (!installationAcceptedByDriver) {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} not accepted,
          </SrText>
        );
      }
      else {
        installDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {installationLink} accepted,
          </SrText>
        );
      }

      if (!mediaRequired) {
        mediaDetail = 'media not requested';
      }
      else if (!mediaAcceptedByDriver) {
        mediaDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {mediaLink} not accepted
          </SrText>
        );
      }
      else {
        mediaDetail = (
          <SrText
            font='greycliff'
            weight='600'
            color='grey'
          >
            {mediaLink} accepted
          </SrText>
        );
      }

      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installDetail}&nbsp;{mediaDetail}
        </SrText>
      );
    }
  };

  const _getDriverAcceptedOrderCheckComponent = () => {
    const {
      checks:{
        orderAccepted: isQualified,
      } = {},
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayAcceptedOrderDetail(!displayAcceptedOrderDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getDriverAcceptedOrderDetail();

    return (
      <SrFlexRow key={`${listingId}_driverAcceptedOrderCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_driverAcceptedOrder`}
          title='Driver Accepted Order'
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayAcceptedOrderDetail && (
          <SrFlexRow
            key={`${listingId}_driverAcceptedOrder_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              <SrText
                font='greycliff'
                weight='600'
                color='grey'
              >
                ({detail})
              </SrText>
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  // Items Installed
  const _getMissingItemNamesList = () => {
    const {
      jobs: {
        missing: missingJobs,
      } = {},
      notInstalledItems = [],
    } = setupHealth;

    const names = [];
    const allMissingJobs = [
      ...missingJobs,
      ...notInstalledItems.map((jobTypeId) => jobTypeId),
    ];

    for (const missingJob of allMissingJobs) {
      const jobName = srJobTypeTextMapping[missingJob];
      if (!names.includes(jobName) && missingJob !== JOB_TYPE_MEDIA) {
        names.push(jobName);
      }
    }

    return names;
  };

  const _getItemsInstalledCheckDetail = () => {
    const {
      installation: {
        isRequired: installationRequired,
      } = {},
    } = setupHealth;

    const itemsList = _getMissingItemNamesList();
    const installationLink = _getInstallLinkComponent({ lowerCase: true });

    if (!installationRequired) {
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          whiteSpace='nowrap'
        >
          (No install order required)
        </SrText>
      );
    }
    if (itemsList.length > 0) {
      const itemsList = _getMissingItemNamesList().join(', ');
      const detailText = itemsList + ' not added to';
      return (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          ({detailText} {installationLink})
        </SrText>
      );
    }
    return (
      <SrText
        font='greycliff'
        weight='600'
        color='grey'
        whiteSpace='nowrap'
      >
        (All added to {installationLink})
      </SrText>
    );
  };

  const _getItemsInstalledCheckComponent = () => {
    const {
      checks:{
        orderItemsInstalled: isQualified,
      } = {},
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayInstalledOrderDetail(!displayInstalledOrderDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getItemsInstalledCheckDetail();

    return (
      <SrFlexRow key={`${listingId}_itemsOnInstallOrderCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_itemsOnInstallOrder`}
          title='Items on Install Order'
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayInstalledOrderDetail && (
          <SrFlexRow
            key={`${listingId}_itemsOnInstallOrder_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              {detail}
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  // Due/Completed on Time
  const _getOnTimeText = ({ daysDiff = 0, prefix = '' }) => {
    if (daysDiff === 0) {
      return ` ${prefix} same day as promise date`;
    }

    if (daysDiff > 0) {
      return ` ${prefix} ${pluralize('day', daysDiff, true)} ` +
          ' after promise date';
    }
    return ` ${prefix} ${pluralize('day', Math.abs(daysDiff), true)}` +
          ' before promise date';

  };

  const _getDueDateOnTimeCheckDetail = () => {
    const {
      media:{
        isRequired: mediaRequired,
        isCreated: mediaCreated,
      } = {},
      installation:{
        isRequired: installationRequired,
        isCreated: installationCreated,
      } = {},
      onTimeDetail:{
        install: {
          allCompleted: allInstallationsCompleted,
          count: {
            completed: completedInstalls,
          } = {},
          orderIds: {
            dueOrder: dueOrderId,
          } = {},
          promisedDateDiffs: {
            dueDate: dueDateInstallationDiff,
          } = {},
        } = {},
        media: {
          completed: mediaCompleted,
          promisedDateDiffs: {
            dueDate: dueDateMediaDiff,
          } = {},
        } = {},
      } = {},
    } = setupHealth;

    let installDetail = '';
    let mediaDetail = '';

    if (!installationRequired) {
      installDetail = 'Install not requested';
    }
    else if (!installationCreated) {
      installDetail = 'Create another install to complete the setup';
    }
    else if (allInstallationsCompleted) {
      const installationLink = _getInstallLinkComponent({});
      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} completed
        </SrText>
      );
    }
    else if (completedInstalls === 0) {
      const dueInstallLink = _getInstallLinkComponent({
        signOtterId: dueOrderId,
      });

      const dueInstallText = _getOnTimeText({
        daysDiff: dueDateInstallationDiff,
        prefix: 'is due',
      });

      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {dueInstallLink} {dueInstallText}
        </SrText>
      );
    }
    else {
      const dueInstallLink = _getInstallLinkComponent({
        lowerCase: true,
        signOtterId: dueOrderId,
      });

      const dueInstallText = _getOnTimeText({
        daysDiff: dueDateInstallationDiff,
        prefix: 'is due',
      });

      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          Partial {dueInstallLink} {dueInstallText}
        </SrText>
      );
    }

    if (!mediaRequired) {
      mediaDetail = ' media not requested';
    }
    else if (!mediaCreated) {
      mediaDetail = ' media not found';
    }
    else {
      const mediaLink = _getMediaLinkComponent();
      const mediaText = mediaCompleted
        ? ' completed'
        : _getOnTimeText({ daysDiff: dueDateMediaDiff, prefix: 'due' });

      mediaDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {mediaLink}
          {mediaText}
        </SrText>
      );
    }

    return (
      <SrText
        font='greycliff'
        weight='600'
        color='grey'
      >
        ({installDetail}, {mediaDetail})
      </SrText>
    );
  };

  const _getDueDateOnTimeCheckComponent = () => {
    const {
      checks:{
        orderDuePromiseDate: isQualified,
      } = {},
      promisedDate,
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayDueOnTimeDetail(!displayDueOnTimeDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getDueDateOnTimeCheckDetail();
    const formattedDate = momentWithAustinTimezone(promisedDate).format('MMM DD');

    return (
      <SrFlexRow key={`${listingId}_dueDateOnTimeCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_dueDateOnTime`}
          title={`Due by ${formattedDate}`}
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayDueOnTimeDetail && (
          <SrFlexRow
            key={`${listingId}_dueDateOnTime_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              {detail}
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  const _getCompletedOnTimeCheckDetail = () => {
    const {
      media:{
        isRequired: mediaRequired,
        isCreated: mediaCreated,
      } = {},
      installation:{
        isRequired: installationRequired,
        isCreated: installationCreated,
      } = {},
      onTimeDetail:{
        install: {
          allCompleted: allInstallationsCompleted,
          count: {
            completed: completedInstalls,
          } = {},
          orderIds: {
            completedOrder: completedOrderId,
          } = {},
          promisedDateDiffs: {
            completedDate: completedInstallationDiff,
          } = {},
        } = {},
        media: {
          completed: mediaCompleted,
          promisedDateDiffs: {
            completedDate: completedMediaDiff,
          } = {},
        } = {},
      } = {},
    } = setupHealth;

    let installDetail = '';
    let mediaDetail = '';

    if (!installationRequired) {
      installDetail = 'Install not requested';
    }
    else if (!installationCreated) {
      installDetail = 'Create another install to complete the setup';
    }
    else if (allInstallationsCompleted) {
      const installationLink = _getInstallLinkComponent({});

      const completedInstallText = _getOnTimeText({
        daysDiff: completedInstallationDiff,
        prefix: 'completed',
      });

      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink}
          {completedInstallText}
        </SrText>
      );
    }
    else if (completedInstalls === 0) {
      const installationLink = _getInstallLinkComponent({});
      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {installationLink} in progress
        </SrText>
      );
    }
    else {
      const completedInstallLink = _getInstallLinkComponent({
        lowerCase: true,
        signOtterId: completedOrderId,
      });

      const completedInstallText = _getOnTimeText({
        daysDiff: completedInstallationDiff,
        prefix: 'completed',
      });

      installDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          Partial {completedInstallLink} {completedInstallText}
        </SrText>
      );
    }

    const mediaLink = _getMediaLinkComponent();

    if (!mediaRequired) {
      mediaDetail = ' media not requested';
    }
    else if (!mediaCreated) {
      mediaDetail = ' media not found';
    }
    else if (!mediaCompleted) {
      mediaDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {mediaLink} in progress
        </SrText>
      );
    }
    else {
      const mediaText = _getOnTimeText({
        daysDiff: completedMediaDiff,
        prefix: 'completed',
      });
      mediaDetail = (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
        >
          {mediaLink}
          {mediaText}
        </SrText>
      );
    }

    return (
      <SrText
        font='greycliff'
        weight='600'
        color='grey'
      >
        ({installDetail}, {mediaDetail})
      </SrText>
    );
  };

  const _getCompletedOnTimeCheckComponent = () => {
    const {
      checks:{
        orderCompletedOnTime: isQualified,
      } = {},
    } = setupHealth;

    const seeMoreComponent = isQualified
      ? (
        <SrText
          font='greycliff'
          weight='600'
          color='grey'
          decoration='underline'
          paddingLeft='2px'
          cursor='pointer'
          onClick={() => setDisplayCompletedOnTimeDetail(!displayCompletedOnTimeDetail)}
        >
          see more
        </SrText>
      )
      : '';

    const detail = _getCompletedOnTimeCheckDetail();

    return (
      <SrFlexRow key={`${listingId}_completedOnTimeCheck`}>
        <SrQualifiedListItem
          key={`${listingId}_completedOnTime`}
          title='Completed on Time'
          isQualified={isQualified}
          detail={seeMoreComponent}
          marginTop='10px'
        />
        {displayCompletedOnTimeDetail && (
          <SrFlexRow
            key={`${listingId}_completedOnTime_detail`}
          >
            <SrBox
              marginLeft='30px'
            >
              {detail}
            </SrBox>
          </SrFlexRow>
        )}
      </SrFlexRow>
    );
  };

  const getChecksComponents = () => {
    const checksComponents = [
      _getCreatedOrderCheckComponent(),
      _getItemsInstalledCheckComponent(),
      _getAssignedOrderCheckComponent(),
      _getDriverAcceptedOrderCheckComponent(),
      _getDueDateOnTimeCheckComponent(),
      _getCompletedOnTimeCheckComponent(),
    ];

    return [...checksComponents];
  };

  const checksComponents = getChecksComponents();

  return (
    <SrFlex
      direction='col'
    >
      <SrFlexRow>
        <SrText
          font='greycliff'
          fontSize='18px'
          weight='800'
        >
          Setup Health
        </SrText>
      </SrFlexRow>
      <SrFlexRow>
        {checksComponents}
      </SrFlexRow>
    </SrFlex>
  );
};

SetupHealthChecks.propTypes = {
  setupHealth: PropTypes.object.isRequired,
  listingId: PropTypes.number.isRequired,
};
export default SetupHealthChecks;
